import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { queryKeys, STREET_VIEW_AUTO_FLAG } from '@vtak/shared';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useMapStore } from '@/stores/map-store';
import { workspaceClient } from '@/services/workspace-client';
import { fetchJson } from '@/services/api-client';
import { CORE_NAV_ITEMS } from '@/constants/navigation';
import { usePluginStore } from '@/stores/plugin-store';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/** Editable Area of Operations bbox. */
interface BboxDraft {
  west: string;
  south: string;
  east: string;
  north: string;
}

/** The slice of the unit-profile response the editor hydrates from. */
interface ProfileFeedScope {
  feedScope?: { bbox?: { west: number; south: number; east: number; north: number } };
  corePanelFlags?: Record<string, boolean>;
}

/** A labelled on/off pill. */
function TogglePill({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11px] transition-colors',
        on
          ? 'border-echo-yellow/50 bg-echo-yellow/15 text-echo-yellow'
          : 'border-echo-border text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

/** Section heading. */
function Heading({ text, hint }: { text: string; hint?: string }): React.JSX.Element {
  return (
    <div>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{text}</h3>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

/**
 * Admin editor for the unit workspace: entitled plugins, visible core panels,
 * default map view, and the Area of Operations bbox that scopes live feeds.
 * Everything persists through the unit profile PATCH (Vertex-backed when the
 * identity platform is the source of record).
 */
export function WorkspaceSettingsEditor({ unitId }: { unitId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const workspace = useWorkspaceStore((s) => s.workspace);
  const activePlugins = usePluginStore((s) => s.activePlugins);
  const activePluginIds = useMemo(
    () => activePlugins.map((plugin) => plugin.id).filter((id) => id !== 'reference'),
    [activePlugins],
  );

  const catalog = useQuery({
    queryKey: ['workspace', 'plugin-catalog'],
    queryFn: () => workspaceClient.getPluginCatalog(),
  });

  const profileKey = useMemo(() => ['workspace', 'unit-profile', unitId] as const, [unitId]);
  const profile = useQuery({
    queryKey: profileKey,
    queryFn: () =>
      fetchJson<ProfileFeedScope | null>(`/api/units/${encodeURIComponent(unitId)}/profile`, null),
  });

  const [plugins, setPlugins] = useState<string[]>([]);
  const [panelFlags, setPanelFlags] = useState<Record<string, boolean>>({});
  const [bbox, setBbox] = useState<BboxDraft>({ west: '', south: '', east: '', north: '' });
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const profileBbox = profile.data?.feedScope?.bbox;
  const profileFlags = profile.data?.corePanelFlags;
  useEffect(() => {
    setPlugins(activePluginIds);
    // Seed from the raw unit profile, not the caller's manifest — the manifest
    // has the admin's PERSONAL my-view overrides merged in, and saving those
    // back would silently write them into unit-wide config.
    setPanelFlags(profileFlags ?? workspace?.corePanelFlags ?? {});
    if (profileBbox) {
      setBbox({
        west: String(profileBbox.west),
        south: String(profileBbox.south),
        east: String(profileBbox.east),
        north: String(profileBbox.north),
      });
    }
  }, [activePluginIds, workspace, profileBbox, profileFlags]);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    setNotice(
      isPartialBbox(bbox)
        ? 'Incomplete AO bbox ignored — fill in all four edges to change the Area of Operations.'
        : null,
    );
    try {
      await workspaceClient.updateProfile(unitId, {
        plugins,
        corePanelFlags: panelFlags,
        ...bboxPatch(bbox),
        ...mapViewPatch(),
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.workspace.config(unitId) });
      await queryClient.invalidateQueries({ queryKey: profileKey });
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch {
      setError('Save failed — check your permissions and try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-5 p-3">
        <section className="space-y-2">
          <Heading text="Plugins" hint="Capabilities this unit is entitled to." />
          <div className="flex flex-wrap gap-1">
            {(catalog.data ?? []).map((plugin) => (
              <TogglePill
                key={plugin.id}
                label={plugin.name}
                on={plugins.includes(plugin.id)}
                onToggle={() => setPlugins((prev) => toggleValue(prev, plugin.id))}
              />
            ))}
            {catalog.isLoading && <p className="text-xs text-muted-foreground">Loading catalog…</p>}
          </div>
        </section>

        <section className="space-y-2">
          <Heading text="Panels" hint="Core side-menu items members see." />
          <div className="flex flex-wrap gap-1">
            {CORE_NAV_ITEMS.filter((item) => !item.hidden && item.id !== 'admin').map((item) => (
              <TogglePill
                key={item.id}
                label={item.label}
                on={panelFlags[item.id] !== false}
                onToggle={() =>
                  setPanelFlags((prev) => ({ ...prev, [item.id]: prev[item.id] === false }))
                }
              />
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <Heading text="Map behavior" hint="How the map reacts as members navigate." />
          <div className="flex flex-wrap gap-1">
            <TogglePill
              label="Street View auto-dive"
              on={panelFlags[STREET_VIEW_AUTO_FLAG] !== false}
              onToggle={() =>
                setPanelFlags((prev) => ({
                  ...prev,
                  [STREET_VIEW_AUTO_FLAG]: prev[STREET_VIEW_AUTO_FLAG] === false,
                }))
              }
            />
          </div>
        </section>

        <section className="space-y-2">
          <Heading
            text="Area of Operations"
            hint="Bounding box that scopes aircraft, vessel, fire, and camera feeds. Applies live."
          />
          <div className="grid grid-cols-2 gap-2">
            {(['west', 'south', 'east', 'north'] as const).map((edge) => (
              <Input
                key={edge}
                value={bbox[edge]}
                placeholder={edge}
                onChange={(e) => setBbox((prev) => ({ ...prev, [edge]: e.target.value }))}
              />
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <Heading text="Default map view" hint="Saving stores the last map click as the unit start view." />
        </section>

        {error && <p className="text-xs text-red-400">{error}</p>}
        {notice && <p className="text-xs text-amber-400">{notice}</p>}
        <Button size="sm" className="w-full gap-1.5" disabled={saving} onClick={save}>
          {flash ? <Check className="h-3.5 w-3.5" /> : null}
          {saving ? 'Saving…' : flash ? 'Saved' : 'Save workspace settings'}
        </Button>
      </div>
    </ScrollArea>
  );
}

/** Toggle a value in a string list. */
function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** The four bbox edge drafts, trimmed, in west/south/east/north order. */
function trimmedEdges(draft: BboxDraft): string[] {
  return [draft.west, draft.south, draft.east, draft.north].map((edge) => edge.trim());
}

/** True when some — but not all — bbox edges are filled in (an incomplete draft). */
function isPartialBbox(draft: BboxDraft): boolean {
  const edges = trimmedEdges(draft);
  const filled = edges.filter((edge) => edge !== '').length;
  return filled > 0 && filled < edges.length;
}

/**
 * A feedScope patch when all four bbox edges are non-blank and numeric; empty
 * otherwise. Any blank edge drops the whole patch — Number('') is 0, so a
 * partially-cleared draft would otherwise save a bogus box at latitude/
 * longitude 0 and silently rescope live feeds.
 */
function bboxPatch(draft: BboxDraft): { feedScope?: { bbox: { west: number; south: number; east: number; north: number } } } {
  if (trimmedEdges(draft).some((edge) => edge === '')) return {};
  const west = Number(draft.west);
  const south = Number(draft.south);
  const east = Number(draft.east);
  const north = Number(draft.north);
  if ([west, south, east, north].some((n) => !Number.isFinite(n))) return {};
  return { feedScope: { bbox: { west, south, east, north } } };
}

/** A mapView patch from the current camera, when the map has one. */
function mapViewPatch(): { mapView?: { lat: number; lng: number; zoom: number } } {
  const view = useMapStore.getState().lastClick;
  const fallback = useWorkspaceStore.getState().workspace?.mapView;
  const source = view ?? fallback;
  if (!source) return {};
  return { mapView: { lat: source.lat, lng: source.lng, zoom: fallback?.zoom ?? 10 } };
}
